import React, { PureComponent } from 'react';
import { defineMessages, injectIntl, WrappedComponentProps } from 'react-intl';
import { Button } from '@lifeomic/chroma-react/components/Button';
import {
  WithStyles,
  createStyles,
  withStyles,
  Theme,
} from '@lifeomic/chroma-react/styles';
import { getTestProps } from '../../../util/testProps';
import { Modal } from '@lifeomic/chroma-react/components/Modal';
import clsx from 'clsx';
import { SurveyOverrideClasses } from './SurveyOverrideClasses';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { withSurveyContext, WithSurveyContextInjectedProps } from './Contexts';

export const testIds = {
  confirmSaveProgress: 'surveySaveProgressDialog-confirm-button',
  cancelSaveProgress: 'surveySaveProgressDialog-cancel-button',
  modal: 'surveySaveProgressDialog-modal',
  saveProgress: 'survey-saveProgress',
};

const messages = defineMessages({
  buttonText: {
    id: 'surveys.saveProgress.buttonText',
    defaultMessage: 'Save and exit',
  },
  cancel: {
    id: 'surveys.saveProgress.cancel',
    defaultMessage: 'Back to survey',
  },
  confirm: {
    id: 'surveys.saveProgress.confirm',
    defaultMessage: 'Exit',
  },
  dialogHeader: {
    id: 'surveys.saveProgress.dialogHeader',
    defaultMessage: 'Are you sure you want to exit the survey?',
  },
  dialogContent: {
    id: 'surveys.saveProgress.dialogContent',
    defaultMessage:
      'The survey is not yet submitted but your answers will be saved so that you can continue where you left off.',
  },
});

export interface DialogText {
  cancelButtonText: string;
  confirmButtonText: string;
  dialogHeader: string;
  dialogContent: string;
  buttonText: string;
}

const styles = (theme: Theme) =>
  createStyles({
    buttonContainer: {
      padding: theme.spacing(2),
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
    },
    headerContainer: {
      padding: theme.spacing(2),
    },
    contentContainer: {
      padding: theme.spacing(2),
      paddingBottom: theme.spacing(4),
    },
  });

export interface SaveProgressButtonOwnProps {
  disabled?: boolean;
  submitIncompleteForm: () => void;
  dialogText: DialogText;
  overrideClasses: SurveyOverrideClasses;
}

export type SaveProgressButtonProps = SaveProgressButtonOwnProps &
  WrappedComponentProps &
  WithStyles<typeof styles> &
  WithSurveyContextInjectedProps;

export interface SaveProgressButtonState {
  isDialogOpen: boolean;
}

class SaveProgressButton extends PureComponent<
  SaveProgressButtonProps,
  SaveProgressButtonState
> {
  public state: SaveProgressButtonState = { isDialogOpen: false };

  handleOpenDialog = () => {
    this.setState({ isDialogOpen: true });
  };

  handleCloseDialog = () => {
    this.setState({ isDialogOpen: false });
  };

  handleSubmit = () => {
    this.handleCloseDialog();
    this.props.submitIncompleteForm();
  };

  render() {
    const {
      classes,
      disabled,
      intl: { formatMessage },
      dialogText = {} as DialogText,
      overrideClasses,
      survey,
    } = this.props;

    const { isDialogOpen } = this.state;

    return (
      <React.Fragment>
        <Button
          className={clsx(overrideClasses.saveProgressButtonClass)}
          style={{ marginRight: '0.75rem' }}
          aria-haspopup="dialog"
          aria-expanded={isDialogOpen}
          disabled={disabled}
          // outlined so Complete/Next is the single solid primary action
          variant="outlined"
          onClick={this.handleOpenDialog}
          {...getTestProps(testIds.saveProgress)}
        >
          {dialogText.buttonText || formatMessage(messages.buttonText)}
        </Button>
        <Modal
          size={0}
          onDismiss={this.handleCloseDialog}
          isOpen={isDialogOpen}
          justifyActions="space-between"
          {...getTestProps(testIds.modal)}
          title={
            survey?.getCustomMessage('submission-header') ||
            dialogText.dialogHeader ||
            formatMessage(messages.dialogHeader)
          }
          actions={
            <>
              <Button
                variant="text"
                onClick={this.handleCloseDialog}
                {...getTestProps(testIds.cancelSaveProgress)}
              >
                {survey?.getCustomMessage('submission-reject') ||
                  dialogText.cancelButtonText ||
                  formatMessage(messages.cancel)}
              </Button>
              <Button
                variant="contained"
                onClick={this.handleSubmit}
                {...getTestProps(testIds.confirmSaveProgress)}
              >
                {survey?.getCustomMessage('submission-accept') ||
                  dialogText.confirmButtonText ||
                  formatMessage(messages.confirm)}
              </Button>
            </>
          }
        >
          <div className={classes.contentContainer}>
            <Text size="body">
              {survey?.getCustomMessage('submission-content') ||
                dialogText.dialogContent ||
                formatMessage(messages.dialogContent)}
            </Text>
          </div>
        </Modal>
      </React.Fragment>
    );
  }
}

export default withSurveyContext(
  injectIntl(withStyles(styles)(SaveProgressButton)),
);
